"use client";

import { useState } from "react";
import { useAuth } from "@/contexts/auth-context";

/**
 * Prompt shown to an email/password user whose address isn't verified yet. The
 * Firebase account exists, but the server gates invite acceptance — and app
 * entry — on a verified email. AuthProvider auto-continues when the tab regains
 * focus after the user clicks the emailed link; this gives them a manual
 * "I've verified" nudge plus a resend. Google sign-in is always verified, so it
 * never reaches here. The caller supplies the surrounding layout/card.
 */
export function VerifyEmailNotice({ email }: { email?: string | null }) {
  const { resendVerificationEmail, refreshVerification, signOut } = useAuth();
  const [busy, setBusy] = useState<"resend" | "check" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onResend() {
    setBusy("resend");
    setNote(null);
    setError(null);
    try {
      await resendVerificationEmail();
      setNote("Verification email sent — check your inbox.");
    } catch {
      setError("Couldn't send the email. Wait a minute and try again.");
    } finally {
      setBusy(null);
    }
  }

  async function onCheck() {
    setBusy("check");
    setNote(null);
    setError(null);
    try {
      // On success this flips needsEmailVerification false and unmounts the
      // notice; if still unverified it's a no-op and we nudge the user.
      await refreshVerification();
      setNote("Still waiting — click the link in your email first, then retry.");
    } catch {
      setError("Couldn't check verification status. Try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="text-center space-y-4 w-full">
      <div className="text-3xl">✉️</div>
      <h2 className="text-lg font-semibold">Verify your email</h2>
      <p className="text-sm text-subtle">
        We sent a verification link to{" "}
        <strong>{email ?? "your email address"}</strong>. Click it, then come
        back here — we&apos;ll continue automatically.
      </p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => void onCheck()}
          disabled={busy !== null}
          className="px-4 py-2.5 rounded-lg bg-accent text-accent-ink text-sm font-medium hover:opacity-95 disabled:opacity-50 transition-colors"
        >
          {busy === "check" ? "…" : "I've verified — continue"}
        </button>
        <button
          type="button"
          onClick={() => void onResend()}
          disabled={busy !== null}
          className="text-xs text-subtle hover:text-foreground disabled:opacity-50"
        >
          {busy === "resend" ? "Sending…" : "Resend verification email"}
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-xs text-subtle hover:text-foreground"
        >
          Use a different account
        </button>
      </div>
      {note && <p className="text-sm text-subtle">{note}</p>}
      {error && (
        <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
      )}
    </div>
  );
}
